import React, { useEffect, useState } from 'react';
import { Button, Divider, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { AddCircleOutline, Info, People, DashboardCustomize } from '@mui/icons-material';
import api from '../../../../api/axios';
import CreateNewsletterModal from './CreateNewsletterModal';
import CreateTemplateModal from './CreateTemplateModal';
import ShowTemplatesModal from './ShowTemplatesModal';
import NewsletterInfoModal from './NewsletterInfoModal';

export interface NewsletterCampaign {
  id: number;
  subject: string;
  sentCount: number;
  totalRecipients: number;
  failedCount: number;
  status: 'SENDING' | 'COMPLETED' | 'DRAFT' | 'SCHEDULED';
  creationDate: string;
  content: string; // HTML content
}

const Newsletter = () => {
  const [campaigns, setCampaigns] = useState<NewsletterCampaign[]>([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [templatesListOpen, setTemplatesListOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // Data states for selection
  const [selectedCampaign, setSelectedCampaign] = useState<NewsletterCampaign | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [editTemplate, setEditTemplate] = useState<any | null>(null);

  // Persistence for the editor state
  const [templateDraft, setTemplateDraft] = useState<any>(null);

  const fetchNewsletterData = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/system/newsletter/campaigns'),
        api.get('/system/newsletter/subscribers/count')
      ]);
      setCampaigns(listRes.data);
      setSubscriberCount(statsRes.data.count);
    } catch (e) {
      console.error("Error fetching newsletter data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletterData();
  }, []);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'SENDING':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'SCHEDULED':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'COMPLETED':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      default:
        return 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20';
    }
  };

  const handleOpenCreateTemplate = () => {
    setEditTemplate(null);
    // Draft will be handled inside the modal or kept if unintentional close occurred
    setCreateTemplateOpen(true);
  };

  const handleEditTemplate = (template: any) => {
    setEditTemplate(template);
    setTemplateDraft(null); // Reset draft to load specific template
    setCreateTemplateOpen(true);
  };

  const handleSelectTemplateForCampaign = (template: any) => {
    setSelectedTemplate(template);
    setTemplatesListOpen(false);
    setCreateCampaignOpen(true);
  };

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-neutral-800/40 p-4 border border-black/5 dark:border-white/5 flex items-center gap-4">
            <div className="p-3 bg-dark-primary/10 rounded-full">
              <People className="text-dark-primary" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase text-neutral-500">Total Subscribers</p>
              <p className="text-xl font-bold dark:text-white">{subscriberCount.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
            Newsletter Campaigns
          </h2>
          <div className="flex gap-2">
            <Button
                variant="outlined"
                color="inherit"
                startIcon={<DashboardCustomize />}
                sx={{ borderRadius: 0, borderColor: 'rgba(255,255,255,0.1)', color: '#9ca3af' }}
                onClick={() => setTemplatesListOpen(true)}
            >
              Manage Templates
            </Button>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutline />}
                sx={{ borderRadius: 0 }}
                onClick={handleOpenCreateTemplate}
            >
              Create Template
            </Button>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5" />

        <TableContainer component={Paper} className="bg-transparent shadow-none mt-4 overflow-hidden">
          <Table size="small" className="border-separate border-spacing-y-2">
            <TableHead>
              <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none">Date</TableCell>
                <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none">Subject</TableCell>
                <TableCell className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none">Status</TableCell>
                <TableCell align="center" className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none">Users (Sent/Total)</TableCell>
                <TableCell align="center" className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none text-red-400">Failed</TableCell>
                <TableCell align="right" className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-none">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((camp) => (
                  <TableRow key={camp.id} className="group bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03] transition-all">
                    <TableCell className="border-none text-[11px] text-neutral-400 font-medium">
                      {new Date(camp.creationDate).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell className="border-none font-bold text-gray-700 dark:text-gray-200 text-xs sm:text-sm">
                      {camp.subject}
                    </TableCell>
                    <TableCell className="border-none">
                  <span className={`text-[9px] px-2 py-0.5 font-black uppercase border ${getStatusStyle(camp.status)}`}>
                    {camp.status}
                  </span>
                    </TableCell>
                    <TableCell align="center" className="border-none font-mono text-xs text-neutral-400">
                      <span className="text-emerald-500 font-bold">{camp.sentCount}</span> / {camp.totalRecipients}
                    </TableCell>
                    <TableCell align="center" className="border-none text-red-500 font-mono text-xs font-bold">
                      {camp.failedCount}
                    </TableCell>
                    <TableCell align="right" className="border-none">
                      <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedCampaign(camp);
                            setInfoOpen(true);
                          }}
                          className="!bg-dark-primary/10 hover:!bg-dark-primary/20"
                          sx={{ borderRadius: 0 }}
                      >
                        <Info sx={{ fontSize: 16 }} className="text-dark-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Template Management Modals */}
        <ShowTemplatesModal
            open={templatesListOpen}
            onClose={() => setTemplatesListOpen(false)}
            onEdit={handleEditTemplate}
            onSelectForCampaign={handleSelectTemplateForCampaign}
        />

        {/* Always rendered to keep the EmailEditor instance alive in the background.
            This prevents 5-10s loading times and state loss on close.
        */}
        <CreateTemplateModal
            open={createTemplateOpen}
            onClose={() => setCreateTemplateOpen(false)}
            onSuccess={() => {
              setTemplateDraft(null);
              fetchNewsletterData();
            }}
            editTemplate={editTemplate}
            draft={templateDraft}
            onDraftChange={setTemplateDraft}
        />

        {/* Campaign Creation Modal */}
        <CreateNewsletterModal
            open={createCampaignOpen}
            selectedTemplate={selectedTemplate}
            onClose={() => setCreateCampaignOpen(false)}
            onSuccess={fetchNewsletterData}
        />

        {/* Campaign Info Modal */}
        {selectedCampaign && (
            <NewsletterInfoModal
                open={infoOpen}
                campaign={selectedCampaign}
                onClose={() => setInfoOpen(false)}
            />
        )}
      </div>
  );
};

export default Newsletter;